import isEqual from "lodash/isEqual";
import type {
  ArtGenresApiSortingEnum,
  CollectionTypeTokensEnum,
  CollectionDataPaginationResponse,
  CollectionPaginationResponse,
  CollectionDetailedSerializer,
  IApiResponse,
  GenreLightSerializer,
  FacetedArtsDataSerializer,
  FacetedArtsResponseSerializer,
  DataPaginationResponse_ArtShortSerializer_,
  PaginationResponse_ArtShortSerializer_,
} from "@litres-libs/types";

import { rtkqApi } from "../api/rtkqApi";
import type { IArtsFacetsRequest } from "../../types/api";
import { TagsType } from "../constants";

export interface ICollectionArtsFacetsRequest extends IArtsFacetsRequest {
  collectionIdOrSlug: number | string;
  genre_id?: number;
  o?: `${ArtGenresApiSortingEnum}`;
  only_drafts_full_free?: boolean;
  tag_ids?: number[];
}

interface ICollectionsQueryArgs {
  limit: number;
  group_type?: string;
  offset?: number;
  offsetTrigger?: boolean;
  type?: `${CollectionTypeTokensEnum}`;
  skipMerge?: boolean;
  rubric_id?: number;
}

export const collectionApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    /** получение списка коллекций */
    getCollectionList: build.query<
      CollectionDataPaginationResponse,
      ICollectionsQueryArgs
    >({
      query: ({ group_type, limit, offset, type, rubric_id }) => ({
        url: rubric_id
          ? `/foundation/api/collections?rubric_id=${rubric_id}`
          : "/foundation/api/collections",
        params: {
          group_type,
          offset,
          limit,
          type,
        },
      }),
      transformResponse: (response: CollectionPaginationResponse) => {
        return response.payload;
      },
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const { group_type, type, rubric_id } = queryArgs;
        return (
          endpointName +
          (group_type ? `_${group_type}` : "") +
          (type ? `_${type}` : "") +
          (rubric_id ? `_${rubric_id}` : "")
        );
      },
      merge: (currentCache, newResponse, { arg }) => {
        if (arg.offsetTrigger) {
          currentCache.data.unshift(...newResponse.data);
        } else if (arg.offset && !arg.skipMerge) {
          currentCache.data.push(...newResponse.data);
        } else {
          currentCache.data = newResponse.data;
        }
        currentCache.pagination = newResponse.pagination;
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
    }),
    /** получение мета-инфы о коллекции по слагу или id */
    getCollection: build.query<
      CollectionDetailedSerializer,
      {
        collectionIdOrSlug: number | string;
      }
    >({
      query: ({ collectionIdOrSlug }) => ({
        url: `/foundation/api/collections/${collectionIdOrSlug}`,
        headers: {
          "accept-version": "2",
        },
      }),
      transformResponse: (
        response: IApiResponse<CollectionDetailedSerializer>
      ) => {
        return response.payload.data;
      },
    }),
    /** получение списка жанров в которых состоят арты коллекции по id коллекции */
    getCollectionGenres: build.query<
      GenreLightSerializer[],
      { collectionIdOrSlug: number | string }
    >({
      query: ({ collectionIdOrSlug }) => ({
        url: `/foundation/api/collections/${collectionIdOrSlug}/genres`,
      }),
      transformResponse: (response: IApiResponse<GenreLightSerializer[]>) => {
        return response.payload.data;
      },
    }),
    /** получение списка артов коллекции по id */
    getCollectionArts: build.query<
      DataPaginationResponse_ArtShortSerializer_,
      ICollectionArtsFacetsRequest
    >({
      query: ({ collectionIdOrSlug, ...params }) => ({
        url: `/foundation/api/collections/${collectionIdOrSlug}/arts`,
        params,
        headers: {
          "accept-version": "2",
        },
      }),
      transformResponse: (response: PaginationResponse_ArtShortSerializer_) => {
        return response.payload;
      },
      providesTags: (result, _err, { collectionIdOrSlug }) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
              { type: TagsType.COLLECTION, id: collectionIdOrSlug },
            ]
          : [
              { type: TagsType.COLLECTION, id: collectionIdOrSlug },
              TagsType.ART_SHORT_LIST,
            ],
    }),
    /** получение списка артов с фасетами коллекции по id для сетки с пагинацией */
    getCollectionFacets: build.query<
      FacetedArtsDataSerializer,
      ICollectionArtsFacetsRequest
    >({
      query: ({ collectionIdOrSlug, ...params }) => ({
        url: `/foundation/api/collections/${collectionIdOrSlug}/arts/facets`,
        params,
        headers: {
          "accept-version": "2",
        },
      }),
      transformResponse: (response: FacetedArtsResponseSerializer) => {
        return response.payload;
      },
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
            ]
          : [TagsType.ART_SHORT_LIST],
    }),
  }),
});
